import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { marketingService } from '../../../services/marketingService';

const ReputationManagement = ({ expanded = false }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('all');

  useEffect(() => {
    loadReviews();
  }, [selectedPlatform]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const data = await marketingService?.getReviews(selectedPlatform === 'all' ? null : selectedPlatform);
      setReviews(data);
    } catch (err) {
      setError(err?.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const averageRating = reviews?.length > 0
    ? (reviews?.reduce((sum, r) => sum + r?.rating, 0) / reviews?.length)?.toFixed(1)
    : 0;

  const ratingDistribution = [5, 4, 3, 2, 1]?.map(rating => ({
    rating,
    count: reviews?.filter(r => r?.rating === rating)?.length || 0
  }));

  const platformStats = [
    { name: 'Google', icon: 'Globe', count: reviews?.filter(r => r?.platform === 'google')?.length || 0, color: 'text-primary' },
    { name: 'Yelp', icon: 'Star', count: reviews?.filter(r => r?.platform === 'yelp')?.length || 0, color: 'text-error' },
    { name: 'Healthgrades', icon: 'Heart', count: reviews?.filter(r => r?.platform === 'healthgrades')?.length || 0, color: 'text-success' },
    { name: 'Facebook', icon: 'Share2', count: reviews?.filter(r => r?.platform === 'facebook')?.length || 0, color: 'text-warning' }
  ];

  const displayReviews = expanded ? reviews : reviews?.slice(0, 4);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="Star"
        size={14}
        className={i < rating ? 'text-warning fill-warning' : 'text-muted'}
      />
    ));
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Star" size={20} className="text-primary" />
          Reputation Management
        </h2>
        <Button variant="outline" size="sm" iconName="Send" iconPosition="left">
          Request Review
        </Button>
      </div>
      <div className="bg-gradient-to-br from-warning/10 to-warning/5 rounded-lg p-4 mb-6 border border-warning/20">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-4xl font-bold text-foreground">{averageRating}</p>
            <div className="flex items-center gap-1 mt-1">
              {renderStars(Math.round(averageRating))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{reviews?.length} reviews</p>
          </div>
          <div className="flex-1 space-y-1">
            {ratingDistribution?.map((dist) => {
              const percentage = reviews?.length > 0 ? ((dist?.count / reviews?.length) * 100)?.toFixed(0) : 0;
              return (
                <div key={dist?.rating} className="flex items-center gap-2">
                  <span className="text-xs text-foreground w-8">{dist?.rating} ★</span>
                  <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-warning h-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-muted-foreground w-8 text-right">{dist?.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Platform Distribution</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {platformStats?.map((platform, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-3 border border-border text-center">
              <Icon name={platform?.icon} size={20} className={`mx-auto mb-2 ${platform?.color}`} />
              <p className="text-xs text-muted-foreground mb-1">{platform?.name}</p>
              <p className="text-lg font-bold text-foreground">{platform?.count}</p>
            </div>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="bg-error/10 border border-error rounded-lg p-3">
          <p className="text-error text-sm">{error}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Recent Reviews</h3>
          {displayReviews?.map((review) => (
            <div key={review?.id} className="bg-muted/30 rounded-lg p-3 border border-border">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-foreground text-sm">{review?.reviewerName}</p>
                    <span className="text-xs text-muted-foreground capitalize">• {review?.platform}</span>
                  </div>
                  <div className="flex items-center gap-1 mb-2">
                    {renderStars(review?.rating)}
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(review?.reviewDate)?.toLocaleDateString()}
                </span>
              </div>
              <p className="text-sm text-foreground mb-2 line-clamp-2">{review?.reviewText}</p>
              {review?.responseText ? (
                <div className="bg-primary/5 rounded-lg p-2 border-l-2 border-primary">
                  <p className="text-xs text-muted-foreground mb-1">Response:</p>
                  <p className="text-xs text-foreground line-clamp-2">{review?.responseText}</p>
                </div>
              ) : (
                <Button variant="ghost" size="xs">
                  Respond
                </Button>
              )}
            </div>
          ))}
          {!expanded && reviews?.length > 4 && (
            <Button variant="ghost" size="sm" fullWidth>
              View All Reviews ({reviews?.length})
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ReputationManagement;